import { useEffect, useState } from "react";
import { X, Loader2, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";

const POSITIONS = ["Attaquant", "Milieu", "Defenseur", "Gardien"];

/**
 * Account-management modal opened from the gear icon in the ProfilePage.
 * Lets the user edit: phone, password (with confirm), position, dominant_foot.
 * Persists via PUT /api/user/settings then refreshes auth context.
 */
export default function SettingsModal({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [position, setPosition] = useState("");
  const [dominantFoot, setDominantFoot] = useState("Droitier");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setName(user.name || "");
    setCity(user.city || "");
    setPhone(user.phone || "");
    setPosition(user.position || "");
    setDominantFoot(user.dominant_foot || "Droitier");
    setPassword(""); setPasswordConfirm("");
  }, [open, user]);

  if (!open) return null;

  const handleSave = async () => {
    if (password && password !== passwordConfirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name, city, phone, position, dominant_foot: dominantFoot,
      };
      // Geocode the city so 20 km match alerts keep working after an edit.
      const trimmedCity = city.trim();
      if (trimmedCity && trimmedCity !== (user.city || "")) {
        try {
          const res = await api.geoForward(trimmedCity, 1);
          const hit = res?.suggestions?.[0];
          if (hit) {
            payload.city = hit.city;
            payload.city_lat = hit.lat;
            payload.city_lng = hit.lng;
          }
        } catch (_) {
          /* keep the raw city string if geocoding fails */
        }
      }
      if (password) {
        payload.password = password;
        payload.password_confirm = passwordConfirm;
      }
      await api.updateAccountSettings(payload);
      await refreshUser();
      toast.success("Parametres enregistres");
      onClose();
    } catch (err) {
      toast.error(err.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    width: '100%', height: 44, padding: '0 12px',
    background: '#0a0a0a', color: '#fff',
    border: '1px solid #27272a', borderRadius: 10, fontSize: 14, outline: 'none',
  };
  const labelStyle = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9ca3af' };

  return (
    <div
      data-testid="settings-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 11500,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        width: '100%', maxWidth: 380, maxHeight: '88vh', overflowY: 'auto',
        background: '#18181b', border: '1px solid #27272a', borderRadius: 18,
        padding: '20px 18px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 className="font-heading italic uppercase" style={{ fontSize: 18, fontWeight: 900, color: '#fff', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 8 }}>
            <SettingsIcon size={18} color="#22c55e" />
            Parametres
          </h2>
          <button data-testid="settings-close" onClick={onClose} aria-label="Fermer" style={{
            width: 32, height: 32, borderRadius: 999, background: '#27272a', color: '#fff',
            border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Nom / Pseudo</label>
            <input data-testid="settings-name" type="text" value={name} placeholder="Ton nom d'affichage"
                   onChange={(e) => setName(e.target.value)} style={{ ...fieldStyle, marginTop: 4 }} />
          </div>
          <div>
            <label style={labelStyle}>Ville</label>
            <input data-testid="settings-city" type="text" value={city} placeholder="Ville"
                   onChange={(e) => setCity(e.target.value)} style={{ ...fieldStyle, marginTop: 4 }} />
          </div>
          <div>
            <label style={labelStyle}>Telephone</label>
            <input data-testid="settings-phone" type="tel" value={phone} placeholder="+33 6 ..."
                   onChange={(e) => setPhone(e.target.value)} style={{ ...fieldStyle, marginTop: 4 }} />
          </div>
          <div>
            <label style={labelStyle}>Poste</label>
            <select data-testid="settings-position" value={position}
                    onChange={(e) => setPosition(e.target.value)} style={{ ...fieldStyle, marginTop: 4 }}>
              <option value="">— Choisir —</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Pied fort</label>
            <select data-testid="settings-foot" value={dominantFoot}
                    onChange={(e) => setDominantFoot(e.target.value)} style={{ ...fieldStyle, marginTop: 4 }}>
              <option value="Droitier">Droitier</option>
              <option value="Gaucher">Gaucher</option>
            </select>
          </div>

          <div style={{ height: 1, background: '#27272a', margin: '6px 0' }} />

          <div>
            <label style={labelStyle}>Nouveau mot de passe</label>
            <input data-testid="settings-password" type="password" value={password}
                   placeholder="Laisser vide pour ne pas changer"
                   onChange={(e) => setPassword(e.target.value)} style={{ ...fieldStyle, marginTop: 4 }} />
          </div>
          {password && (
            <div>
              <label style={labelStyle}>Confirmation</label>
              <input data-testid="settings-password-confirm" type="password" value={passwordConfirm}
                     onChange={(e) => setPasswordConfirm(e.target.value)} style={{ ...fieldStyle, marginTop: 4 }} />
            </div>
          )}
        </div>

        <button
          data-testid="settings-save"
          onClick={handleSave}
          disabled={saving}
          style={{
            marginTop: 18, width: '100%', height: 48, borderRadius: 14,
            background: 'linear-gradient(180deg,#22c55e 0%,#16a34a 100%)',
            color: '#fff', fontWeight: 800, fontSize: 14,
            border: 'none', cursor: saving ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: '0 6px 16px rgba(34,197,94,0.35)',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}
